// Loads Google reCAPTCHA v3 via a plain script tag (no npm package, so no
// version-mismatch risk) and provides a helper to fetch a token for a
// given action right before form submission.

declare global {
  interface Window {
    grecaptcha?: {
      ready: (cb: () => void) => void;
      execute: (siteKey: string, options: { action: string }) => Promise<string>;
    };
  }
}

const DEFAULT_SITE_KEY = '6LdmJoMtAAAAANbYKOqC64jCyDO9CFfe6Aiz2Egd';

let siteKeyCache: string | null = null;
let scriptLoadPromise: Promise<void> | null = null;

const getSiteKey = async (): Promise<string> => {
  if (siteKeyCache) return siteKeyCache;
  let resolvedKey: string = DEFAULT_SITE_KEY;
  try {
    const { getPublicSetting } = await import('./publicSettings');
    resolvedKey = await getPublicSetting('recaptcha_site_key', DEFAULT_SITE_KEY);
  } catch {
    resolvedKey = DEFAULT_SITE_KEY;
  }
  siteKeyCache = resolvedKey;
  return resolvedKey;
};

const loadScript = (siteKey: string): Promise<void> => {
  if (scriptLoadPromise) return scriptLoadPromise;
  scriptLoadPromise = new Promise((resolve) => {
    if (window.grecaptcha) {
      resolve();
      return;
    }
    const script = document.createElement('script');
    script.src = `https://www.google.com/recaptcha/api.js?render=${siteKey}`;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => resolve(); // fail open — form still works without the token
    document.head.appendChild(script);
  });
  return scriptLoadPromise;
};

// Call this right before submitting a protected form. Returns an empty
// string if reCAPTCHA isn't configured or fails to load — the backend
// treats a missing token as "not configured" and skips this layer,
// falling back to the honeypot + rate limiting checks only.
export const getRecaptchaToken = async (action: string): Promise<string> => {
  try {
    const siteKey = await getSiteKey();
    await loadScript(siteKey);
    if (!window.grecaptcha) return '';

    return await new Promise<string>((resolve) => {
      window.grecaptcha!.ready(() => {
        window.grecaptcha!
          .execute(siteKey, { action })
          .then(resolve)
          .catch(() => resolve(''));
      });
    });
  } catch {
    return '';
  }
};